import { useEffect, useRef } from "react";

/**
 * Global hand-scanner input listener.
 * Hand HID scanners type rapidly (≤50ms between keys) and finish with Enter.
 * - Pauses when an input/textarea/contenteditable is focused
 * - Pauses when `disabled` is true (e.g. a modal is open)
 */
export function useScannerInput(
  onScan: (token: string) => void,
  options?: { disabled?: boolean; maxGapMs?: number; minLength?: number },
) {
  const onScanRef = useRef(onScan);
  onScanRef.current = onScan;
  const disabled = options?.disabled ?? false;
  const maxGap = options?.maxGapMs ?? 50;
  const minLen = options?.minLength ?? 3;

  useEffect(() => {
    if (disabled) return;
    let buffer = "";
    let lastTime = 0;

    const isEditable = (el: EventTarget | null) => {
      if (!(el instanceof HTMLElement)) return false;
      const tag = el.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
      if (el.isContentEditable) return true;
      return false;
    };

    const handler = (e: KeyboardEvent) => {
      if (isEditable(document.activeElement)) return;
      const now = performance.now();
      if (now - lastTime > maxGap && e.key !== "Enter") {
        buffer = "";
      }
      lastTime = now;

      if (e.key === "Enter") {
        const token = buffer.trim();
        buffer = "";
        if (token.length >= minLen) {
          e.preventDefault();
          onScanRef.current(token);
        }
        return;
      }

      if (e.key.length === 1) {
        buffer += e.key;
      }
    };

    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [disabled, maxGap, minLen]);
}
